import { FC, useEffect } from 'react';
import { FullscreenReferenceType } from '../../../types/types';
import FullscreenReference from '../../../components/FullscreenReference/FullscreenReference';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useDataset } from '../../../api/hooks/useDataset';
import Loading from '../../../components/Loading/Loading';
import ApiError from '../../../components/ApiError/ApiError';
import { useTranslate } from '../../../hooks/context/useTranslate';

interface ReferenceProps {
  fullscreenReference: FullscreenReferenceType | null;
  handleCloseFullscreenReference: () => void;
  handleOnSubmit: (value: string) => void;
  autoSelectInsertedId?: string;
  breadcrumbsHeight?: number;
}

const Reference: FC<ReferenceProps> = ({
  fullscreenReference,
  handleCloseFullscreenReference,
  handleOnSubmit,
  autoSelectInsertedId,
  breadcrumbsHeight,
}) => {
  const { referenceDatasets, setReferenceDatasets } = useDatasetContext();
  const { t } = useTranslate();

  const noAvaliableReferenceDataset =
    !fullscreenReference?.control?.datasetId ||
    !referenceDatasets[fullscreenReference.control.datasetId];

  const {
    fetch: fetchDataset,
    error: datasetError,
    isLoading: datasetLoading,
  } = useDataset(fullscreenReference?.control?.datasetId || '');

  useEffect(() => {
    const fetchReferenceDataset = async (referenceDatasetId: string) => {
      const referenceDataset = await fetchDataset();
      if (referenceDataset) {
        setReferenceDatasets((oldState) => ({
          ...oldState,
          [referenceDatasetId]: referenceDataset,
        }));
      }
    };

    if (noAvaliableReferenceDataset && fullscreenReference) {
      fetchReferenceDataset(fullscreenReference.control.datasetId);
    }
  }, [noAvaliableReferenceDataset, fullscreenReference]);

  if (datasetError) {
    return <ApiError error={datasetError.error} />;
  }

  if (!fullscreenReference || noAvaliableReferenceDataset || datasetLoading) {
    return <Loading message={t('loadingReference')} />;
  }

  return (
    <FullscreenReference
      datasetId={fullscreenReference.control.datasetId}
      control={fullscreenReference.control}
      closeFullscreenReference={handleCloseFullscreenReference}
      handleOnSubmit={handleOnSubmit}
      value={fullscreenReference.value}
      referenceDatasets={referenceDatasets}
      setReferenceDatasets={setReferenceDatasets}
      label={fullscreenReference.label}
      autoSelectInsertedId={autoSelectInsertedId}
      isFormInput={fullscreenReference.isFormInput}
      breadcrumbsHeight={breadcrumbsHeight}
    />
  );
};

export default Reference;
